/**
 * Client Portal Service
 *
 * API client pour le portail client (maîtres d'ouvrage)
 * Utilise un token JWT séparé de l'app principale
 */

import type {
  MagicLinkRequestResult,
  MagicLinkVerifyResult,
  ClientPortalUser,
  ClientProject,
  ClientSituation,
  PendingSituation,
  ValidateSituationInput,
  ClientMessage,
  ClientConversation,
  SendMessageInput,
  ClientDashboardStats,
  ClientDocument,
  ClientDocumentsFilter,
  ClientDocumentStats,
  ClientProjectTimeline,
  ClientPhase,
  ClientPhoto,
} from '../types/client-portal.types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const PORTAL_BASE = `${API_BASE}/portal`;

// Token management pour le portail client (séparé de l'app principale)
const PORTAL_TOKEN_KEY = 'client_portal_token';

export const getPortalToken = (): string | null => {
  return sessionStorage.getItem(PORTAL_TOKEN_KEY);
};

export const setPortalToken = (token: string): void => {
  sessionStorage.setItem(PORTAL_TOKEN_KEY, token);
};

export const clearPortalToken = (): void => {
  sessionStorage.removeItem(PORTAL_TOKEN_KEY);
};

// Client fetch avec auth portail
async function portalFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getPortalToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${PORTAL_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error ${response.status}`);
  }

  const data = await response.json();
  return data.data !== undefined ? data.data : data;
}

// ==================== AUTH ====================

export const clientPortalAuthService = {
  /**
   * Demande un magic link par email
   */
  requestMagicLink: async (email: string, companyId: string): Promise<MagicLinkRequestResult> => {
    return portalFetch<MagicLinkRequestResult>('/auth/magic-link/request', {
      method: 'POST',
      body: JSON.stringify({ email, companyId }),
    });
  },

  /**
   * Vérifie un magic link et retourne le token de session
   */
  verifyMagicLink: async (token: string): Promise<MagicLinkVerifyResult> => {
    const response = await fetch(`${PORTAL_BASE}/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
    const data = await response.json();

    if (data.success && data.token) {
      setPortalToken(data.token);
    }

    return data;
  },

  /**
   * Récupère les infos de l'utilisateur connecté
   */
  getMe: async (): Promise<ClientPortalUser> => {
    return portalFetch<ClientPortalUser>('/me');
  },

  /**
   * Déconnexion
   */
  logout: async (): Promise<void> => {
    try {
      await portalFetch<void>('/auth/logout', { method: 'POST' });
    } finally {
      clearPortalToken();
    }
  },

  /**
   * Prolonge la session
   */
  extendSession: async (): Promise<{ expiresAt: string }> => {
    return portalFetch<{ expiresAt: string }>('/auth/extend', { method: 'POST' });
  },
};

// ==================== DASHBOARD ====================

export const clientPortalDashboardService = {
  /**
   * Récupère les stats du dashboard
   */
  getStats: async (): Promise<ClientDashboardStats> => {
    return portalFetch<ClientDashboardStats>('/dashboard');
  },
};

// ==================== PROJECTS ====================

export const clientPortalProjectsService = {
  /**
   * Liste des projets du client
   */
  getAll: async (): Promise<ClientProject[]> => {
    return portalFetch<ClientProject[]>('/projects');
  },

  /**
   * Détail d'un projet
   */
  getById: async (id: string): Promise<ClientProject> => {
    return portalFetch<ClientProject>(`/projects/${id}`);
  },
};

// ==================== SITUATIONS ====================

export const clientPortalSituationsService = {
  /**
   * Liste des situations en attente de validation
   */
  getPending: async (): Promise<PendingSituation[]> => {
    return portalFetch<PendingSituation[]>('/situations/pending');
  },

  /**
   * Liste des situations d'un projet
   */
  getByProject: async (projectId: string): Promise<ClientSituation[]> => {
    return portalFetch<ClientSituation[]>(`/projects/${projectId}/situations`);
  },

  /**
   * Détail d'une situation
   */
  getById: async (projectId: string, situationId: string): Promise<ClientSituation> => {
    return portalFetch<ClientSituation>(`/projects/${projectId}/situations/${situationId}`);
  },

  /**
   * Valider une situation avec signature
   */
  validate: async (
    projectId: string,
    situationId: string,
    input: ValidateSituationInput
  ): Promise<ClientSituation> => {
    return portalFetch<ClientSituation>(
      `/projects/${projectId}/situations/${situationId}/validate`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },

  /**
   * Rejeter une situation
   */
  reject: async (
    projectId: string,
    situationId: string,
    reason: string
  ): Promise<ClientSituation> => {
    return portalFetch<ClientSituation>(
      `/projects/${projectId}/situations/${situationId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'REJECTED', reason }),
      }
    );
  },

  /**
   * Contester une situation
   */
  dispute: async (
    projectId: string,
    situationId: string,
    reason: string
  ): Promise<ClientSituation> => {
    return portalFetch<ClientSituation>(
      `/projects/${projectId}/situations/${situationId}/dispute`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'DISPUTED', reason }),
      }
    );
  },
};

// ==================== MESSAGES ====================

export const clientPortalMessagesService = {
  /**
   * Liste des conversations
   */
  getConversations: async (): Promise<ClientConversation[]> => {
    return portalFetch<ClientConversation[]>('/messages/conversations');
  },

  /**
   * Nombre de messages non lus
   */
  getUnreadCount: async (): Promise<{ count: number }> => {
    return portalFetch<{ count: number }>('/messages/unread-count');
  },

  /**
   * Messages d'un projet
   */
  getByProject: async (projectId: string): Promise<ClientMessage[]> => {
    return portalFetch<ClientMessage[]>(`/projects/${projectId}/messages`);
  },

  /**
   * Envoyer un message
   */
  send: async (projectId: string, input: SendMessageInput): Promise<ClientMessage> => {
    return portalFetch<ClientMessage>(`/projects/${projectId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

// ==================== DOCUMENTS (Sprint 3.3 - US-3.3.3) ====================

export const clientPortalDocumentsService = {
  /**
   * Liste des documents d'un projet
   */
  getByProject: async (
    projectId: string,
    filter?: ClientDocumentsFilter
  ): Promise<ClientDocument[]> => {
    const params = new URLSearchParams();
    if (filter?.documentType) {
      params.append('documentType', filter.documentType);
    }
    if (filter?.search) {
      params.append('search', filter.search);
    }
    const queryString = params.toString();
    const endpoint = `/projects/${projectId}/documents${queryString ? `?${queryString}` : ''}`;
    return portalFetch<ClientDocument[]>(endpoint);
  },

  /**
   * Statistiques des documents d'un projet
   */
  getStats: async (projectId: string): Promise<ClientDocumentStats> => {
    return portalFetch<ClientDocumentStats>(`/projects/${projectId}/documents/stats`);
  },

  /**
   * Détail d'un document
   */
  getById: async (documentId: string): Promise<ClientDocument> => {
    return portalFetch<ClientDocument>(`/documents/${documentId}`);
  },

  /**
   * Documents récents sur tous les projets
   */
  getRecent: async (limit: number = 10): Promise<ClientDocument[]> => {
    return portalFetch<ClientDocument[]>(`/documents/recent?limit=${limit}`);
  },
};

// ==================== TIMELINE (Sprint 3.3 - US-3.3.1) ====================

export const clientPortalTimelineService = {
  /**
   * Timeline complète d'un projet (phases + photos récentes)
   */
  getTimeline: async (projectId: string): Promise<ClientProjectTimeline> => {
    return portalFetch<ClientProjectTimeline>(`/projects/${projectId}/timeline`);
  },

  /**
   * Liste des phases d'un projet
   */
  getPhases: async (projectId: string): Promise<ClientPhase[]> => {
    return portalFetch<ClientPhase[]>(`/projects/${projectId}/phases`);
  },

  /**
   * Photos d'un projet
   */
  getPhotos: async (projectId: string, limit?: number): Promise<ClientPhoto[]> => {
    const params = limit ? `?limit=${limit}` : '';
    return portalFetch<ClientPhoto[]>(`/projects/${projectId}/photos${params}`);
  },

  /**
   * Détail d'une photo
   */
  getPhoto: async (photoId: string): Promise<ClientPhoto> => {
    return portalFetch<ClientPhoto>(`/photos/${photoId}`);
  },
};

// Export global
export const clientPortalService = {
  auth: clientPortalAuthService,
  dashboard: clientPortalDashboardService,
  projects: clientPortalProjectsService,
  situations: clientPortalSituationsService,
  messages: clientPortalMessagesService,
  documents: clientPortalDocumentsService,
  timeline: clientPortalTimelineService,
};
